import { Dialog, DialogActions, DialogContent, DialogTitle } from "@mui/material";

import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { useCallback, useEffect } from "react";
import { ModalLoading } from "../../../../../../common/components/modal_loading";


import PrimaryButton from "../../../../../../common/components/primary_button";
import FormContainer from "../../../../../../common/components/form_container";
import DefaultFormInput from "../../../../../../common/components/default_form_input";
import { DiarioObrasImagensForm, DiarioObrasImagensSchema } from "../../../schemas/DiarioObrasImagens/diario_obras_imagens";
import CommonConstants from "../../../../../../common/constants";
import styled from "@emotion/styled";
import { LogTipoTabela } from "../../../../../../common/utils/log_tabela_tipo";
import { isNotNullOrUndefined } from "../../../../../../common/utils/utils";


const VisuallyHiddenInput = styled('input')({
  clip: 'rect(0 0 0 0)',
  clipPath: 'inset(50%)',
  height: 1,
  overflow: 'hidden',
  position: 'absolute',
  bottom: 0,
  left: 0,
  whiteSpace: 'nowrap',
  width: 1,
});
type Props = {
  open: boolean,
  saving: boolean,
  selectedImagens: DiarioObrasImagensForm | null,
  IdDiarioObras: number;
  fileUploadUrl: string;
  onSubmit: (form: DiarioObrasImagensForm) => void,
  handleStartFileUpload: (file: File | null) => void,
  handleDeleteFileUpload: () => void,
  onOpenChangeHistory: (id: number, tabelaTipo: LogTipoTabela) => void,
  onClose: () => void,
};

export const ImagensModal = ({
  open,
  saving,
  IdDiarioObras,
  selectedImagens,
  fileUploadUrl,
  onOpenChangeHistory,
  onSubmit,
  onClose,
  handleStartFileUpload,
  handleDeleteFileUpload
}: Props) => {
  const hookForm = useForm<DiarioObrasImagensForm>({
    mode: "onSubmit",
    reValidateMode: "onSubmit",
    resolver: zodResolver(DiarioObrasImagensSchema),
  });

  useEffect(() => {
    if (open) {
      if (selectedImagens) {
        hookForm.reset({
          IdDiarioObrasImagens: selectedImagens.IdDiarioObrasImagens,
          IdDiarioObras: selectedImagens.IdDiarioObras,
          DescricaoImagem: selectedImagens.DescricaoImagem,
          ExtensaoImagem: selectedImagens.ExtensaoImagem,
          Nome: selectedImagens.Nome,
          Ordem: selectedImagens.Ordem,
        });
      }
    } else {
      hookForm.reset({
        IdDiarioObrasImagens: undefined,
        IdDiarioObras: IdDiarioObras,
        DescricaoImagem: "",
        ExtensaoImagem: "",
        Nome: "",
        Ordem: 0,
      });
    }
  }, [hookForm, open, IdDiarioObras, selectedImagens]);

  const handleChangeHistory = useCallback(() => {
    if (selectedImagens) {
      onOpenChangeHistory(selectedImagens.IdDiarioObrasImagens, LogTipoTabela.DIARIO_OBRAS_IMAGENS);
    }
  }, [selectedImagens, onOpenChangeHistory]);

  const handleSubmit = useCallback((form: DiarioObrasImagensForm) => {
    if (!saving) {
      onSubmit(form);
      hookForm.reset();
    }
  }, [hookForm, onSubmit, saving]);

  const handleClose = useCallback(() => {
    if (!saving) {
      onClose();
      hookForm.reset();
    }
  }, [hookForm, onClose, saving]);

  return (
    <Dialog
      open={open}
      fullWidth={true}
      maxWidth="md"
      slotProps={{
        paper: {
          component: 'form',
          onSubmit: hookForm.handleSubmit(handleSubmit),
        }
      }}
      sx={{
        "& .MuiDialog-container": {
          "& .MuiPaper-root": {
            width: "100%",
            maxWidth: "42rem",
          },
        },
      }}
    >
      <DialogTitle>
        Imagens
      </DialogTitle>

      <DialogContent>
        <ModalLoading loading={saving}>
          <FormContainer title={selectedImagens ? "Editar" : "Cadastrar"} arrangement="fullRow">
            <>
              <div className="w-full bg-light-mode-2 dark:bg-dark-mode-2 p-4 rounded-[4px] border-[1px] border-border-light-mode dark:border-border-dark-mode mb-4">
                <div className="w-[16rem] h-[12rem] border border-gray-300 py-2 px-6">
                  <div className="w-full h-full" onClick={() => document.getElementById('profileInput')?.click()}>
                    {fileUploadUrl
                      ? (
                        <img
                          src={fileUploadUrl}
                          className="w-full h-full object-contain bg-center bg-no-repeat"
                          alt="User profile" />
                      ) : (
                        <img
                          src={CommonConstants.assets.noAvatar}
                          className="w-full h-full object-contain bg-center bg-no-repeat"
                          alt="" />
                      )}
                  </div>
                </div>
                <div className="w-full flex flex-row justify-between mt-2">
                  <p className="flex-1 text-black dark:text-white">São permitidas apenas imagens com extensão .png, .jpeg, .jpg, .gif, .img!</p>
                  <div className="flex flex-row gap-2">
                    <div className="w-32">
                      <PrimaryButton
                        component="label"
                        id='profileInput'
                        buttonColor="primary-green"
                      >
                        <VisuallyHiddenInput
                          type="file"
                          id='profileImageInput'
                          onChange={(e) => {
                            handleStartFileUpload(e.target.files?.item(0) ?? null);
                            e.target.value = '';
                          }}
                          accept=".png,.jpeg,.jpg,.gif,.img"
                          multiple={false}
                        />
                        Selecionar
                      </PrimaryButton>
                    </div>

                    {/* Botão Excluir */}
                    <div className="w-32">
                      <PrimaryButton
                        type="button"
                        onClick={() => handleDeleteFileUpload()}
                        buttonColor='red'
                      >
                        Excluir
                      </PrimaryButton>
                    </div>
                  </div>
                </div>
              </div>
              <div className="md:col-span-2">
                <DefaultFormInput
                  name='DescricaoImagem'
                  label="Descricao"
                  reactHookForm={hookForm}
                />
              </div>
              <DefaultFormInput
                name='Ordem'
                label="Ordem"
                reactHookForm={hookForm}
              />
            </>
          </FormContainer>
        </ModalLoading>
      </DialogContent>

      <DialogActions>
        <div className="w-full h-full items-center justify-between flex p-4 gap-4">
          <div className="w-[220px]">
            {
              isNotNullOrUndefined(selectedImagens) &&
              <PrimaryButton
                buttonColor="yellow"
                onClick={handleChangeHistory}>
                <span className="text-xs">
                  Historico de alterações
                </span>
              </PrimaryButton>
            }
          </div>
          <div className="flex flex-row gap-2 w-full justify-end">
            <div className="p-2 hover:bg-hover-light-gray cursor-pointer dark:hover:bg-border-dark-gray rounded-[4px] font-semibold">
              <h1
                className="text-sm text-black dark:text-white"
                onClick={handleClose}>
                Cancelar
              </h1>
            </div>

            <div className="w-[120px]">
              {saving ? (
                <PrimaryButton
                  buttonColor="primary-green">
                  Salvando...
                </PrimaryButton>
              ) : (
                <PrimaryButton
                  type="submit"
                  buttonColor="primary-green">
                  Salvar
                </PrimaryButton>
              )}
            </div>
          </div>
        </div>
      </DialogActions>
    </Dialog>
  );
}